import { Button } from '@/components/ui/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useProjects } from '@/hooks/use-projects';
import { useRailNav } from '@/lib/rail-nav';
import { useThemeStore } from '@/stores/theme';
import { BookOpen, Download, Monitor, Moon, Plus, SearchIcon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * ⌘K: one command palette for the whole console.
 *
 * The header carries a compact trigger (as the reference does) rather than a
 * permanent text field — a 400px input in the top bar spent the widest part of
 * the header on the console's least-used control, while the list pages already
 * have their own query box with the key:value help next to the data.
 *
 * The palette reads its destinations from `useRailNav`, so a page is reachable
 * here the moment it appears in the rail.
 */
export function CommandMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { items } = useRailNav();
  const { data: projects = [] } = useProjects();
  const setMode = useThemeStore((s) => s.setMode);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const run = (action: () => void) => {
    setOpen(false);
    setQuery('');
    action();
  };
  const trimmed = query.trim();

  return (
    <>
      <Button
        variant="outline"
        aria-keyshortcuts="Meta+K Control+K"
        onClick={() => setOpen(true)}
        className="group relative h-8 w-full flex-1 justify-start rounded-md bg-muted/25 pe-12 font-normal text-muted-foreground shadow-none hover:bg-accent sm:w-40 md:flex-none lg:w-52 xl:w-64"
      >
        <SearchIcon aria-hidden className="absolute inset-s-2 size-4" />
        <span className="ms-4 truncate">{t('command.trigger', 'Search')}</span>
        <kbd className="pointer-events-none absolute inset-e-1.5 top-1/2 hidden h-5 -translate-y-1/2 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-2xs font-medium group-hover:bg-accent sm:flex">
          ⌘K
        </kbd>
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
        title={t('command.trigger', 'Search')}
        description={t('command.hint', 'Jump to a page or a project, or search events.')}
        className="sm:max-w-lg"
      >
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder={t('command.placeholder', 'Search events, pages and projects…')}
        />
        <CommandList>
          <CommandEmpty>{t('command.empty', 'No results.')}</CommandEmpty>

          {trimmed && (
            <CommandGroup heading={t('command.search_group', 'Search')}>
              <CommandItem
                value={`events:${trimmed}`}
                onSelect={() => run(() => navigate(`/events?q=${encodeURIComponent(trimmed)}`))}
              >
                <SearchIcon aria-hidden />
                {t('command.search_events', 'Search events for "{{query}}"', {
                  query: trimmed,
                })}
              </CommandItem>
            </CommandGroup>
          )}

          <CommandGroup heading={t('command.navigation', 'Navigation')}>
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.key}
                  value={`${item.label} ${item.hint ?? ''}`}
                  aria-description={item.hint}
                  onSelect={() => run(() => navigate(item.to ?? item.key))}
                >
                  <Icon aria-hidden />
                  <span className="flex min-w-0 flex-col">
                    <span>{item.label}</span>
                    {item.hint && (
                      <span className="text-xs text-muted-foreground">{item.hint}</span>
                    )}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>

          {/* The rail is destinations only; the one action the console owns
              lives here as well as in the events band, so it stays reachable
              from any page. */}
          <CommandGroup heading={t('command.actions', 'Actions')}>
            <CommandItem
              value={t('nav.submit')}
              // Same shape as the events button: the panel opens over a list,
              // and that list's scope has to ride along untouched.
              onSelect={() => run(() => navigate({ pathname: '/submit', search: location.search }))}
            >
              <Plus aria-hidden />
              {t('nav.submit')}
            </CommandItem>
          </CommandGroup>

          {projects.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={t('command.projects', 'Projects')}>
                {projects.map((project) => (
                  <CommandItem
                    key={project.id}
                    value={`${project.name} ${project.public_id}`}
                    onSelect={() => run(() => navigate(`/projects/${project.public_id}/dashboard`))}
                  >
                    <span
                      aria-hidden
                      className="flex size-5 items-center justify-center rounded-sm bg-sidebar-accent text-2xs font-semibold"
                    >
                      {project.name[0]?.toUpperCase()}
                    </span>
                    {project.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          <CommandSeparator />
          <CommandGroup heading={t('command.theme', 'Theme')}>
            <CommandItem onSelect={() => run(() => setMode('light'))}>
              <Sun aria-hidden />
              {t('nav.theme_light')}
            </CommandItem>
            <CommandItem onSelect={() => run(() => setMode('dark'))}>
              <Moon aria-hidden />
              {t('nav.theme_dark')}
            </CommandItem>
            <CommandItem onSelect={() => run(() => setMode('system'))}>
              <Monitor aria-hidden />
              {t('nav.theme_system')}
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />
          <CommandGroup heading={t('command.links', 'Links')}>
            <CommandItem
              onSelect={() => run(() => window.open('/docs', '_blank', 'noopener,noreferrer'))}
            >
              <BookOpen aria-hidden />
              {t('public.nav_docs')}
            </CommandItem>
            <CommandItem onSelect={() => run(() => navigate('/download'))}>
              <Download aria-hidden />
              {t('public.nav_download')}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
